import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { financialAccountContract } from "@common/contracts/financial-account.contract";
import { FinancialAccountDto } from "@common/dto/financialAccount/financial-account.dto";
import { FinancialAccount } from "@common/types/financialAccount";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useScope } from "@state/scopeContext.lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    entitiesAccountsQueryKey,
    updateFinancialAccountInQueryData,
} from "@queries/entitiesAccounts";
import { invalidateAccountRelatedQueries } from "./account.api";

export const financialAccountContractClient = getBackendAPIClient(
    financialAccountContract,
);

export function useExtractMergedAccountMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialAccountContract.extractMergedAccount
            >["body"],
        ) => {
            const response =
                await financialAccountContractClient.extractMergedAccount({
                    body,
                });

            return {
                extractedFinancialAccount: FinancialAccountDto.deserialize(
                    response.body.extractedFinancialAccount,
                ),
            };
        },
        onSuccess: ({ extractedFinancialAccount }) => {
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            invalidateAccountRelatedQueries({
                entityIds: extractedFinancialAccount.entityId
                    ? [extractedFinancialAccount.entityId]
                    : undefined,
                workspaceId,
            });
        },
    });
}

interface UseFinancialAccountUpdateMutationOptions {
    optimisticUpdate?: boolean;
    onSuccess?: (updatedAccount: FinancialAccount) => void;
}

export function useFinancialAccountUpdateMutation(
    financialAccount: FinancialAccount,
    {
        optimisticUpdate,
        onSuccess,
    }: UseFinancialAccountUpdateMutationOptions = {},
) {
    const queryClient = useQueryClient();
    const { isViewOnly } = useViewOnlyRefusal();
    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialAccountContract.updateAccount
            >["body"],
        ) => {
            const response = await financialAccountContractClient.updateAccount(
                {
                    params: { financialAccountId: financialAccount.id },
                    body,
                },
            );
            return FinancialAccountDto.deserialize(response.body);
        },
        onMutate: (body) => {
            // View-only is refused by the API; an optimistic write would only
            // flash. Returning without a snapshot also makes the rollback in
            // `onError` a no-op.
            if (isViewOnly) {
                return;
            }
            if (optimisticUpdate) {
                updateFinancialAccountInQueryData(queryClient, {
                    ...financialAccount,
                    ...body,
                });
            }
            return financialAccount;
        },
        onSuccess: (updatedAccount, body) => {
            if (!optimisticUpdate) {
                updateFinancialAccountInQueryData(queryClient, {
                    ...financialAccount,
                    ...body,
                });
            }
            onSuccess?.(updatedAccount);
        },
        onError: (_1, _2, previousAccount) => {
            if (optimisticUpdate && previousAccount) {
                updateFinancialAccountInQueryData(queryClient, previousAccount);
            }
        },
    });
}
